/**
 * Sign in. Magic link only — no passwords exist to type, store, or leak (SECURITY.md 2).
 *
 * The response is identical whether or not the address is known, so this form cannot be used to
 * work out who has an account.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../api.js';
import type { EnvironmentBadge as Badge } from '../api.js';
import { EnvironmentBadges } from '../components/Shell.js';

export function SignInPage({ badge }: { badge: Badge | null }): ReactNode {
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    const email = String(new FormData(event.currentTarget).get('email') ?? '');
    try {
      await api.post('/api/auth/sign-in', { email });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a sign-in link.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="signin">
      <div className="card signin-card">
        <div className="brand">
          <span className="brand-mark">SIXGEN</span>
          <span className="brand-sub">AI Operating System</span>
        </div>
        <EnvironmentBadges badge={badge} />

        {sent ? (
          <>
            <h1>Check your email</h1>
            <p className="muted">
              If that address has an account, a sign-in link is on its way. It expires in fifteen
              minutes.
            </p>
            {badge?.environment !== 'production' && (
              <p className="muted small">
                Email delivery arrives in Phase 2. Until then the link is printed in the API server
                log.
              </p>
            )}
          </>
        ) : (
          <>
            <h1>Sign in</h1>
            <form onSubmit={(e) => void onSubmit(e)}>
              <label>
                Email
                <input name="email" type="email" required autoComplete="email" autoFocus />
              </label>
              {error && <div className="alert">{error}</div>}
              <button type="submit" disabled={pending}>
                {pending ? 'Sending…' : 'Send sign-in link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
